import {
  Loader,
  NotificationTypeEnum,
  useNotification,
} from '@prismamedia/one-components';
import { useState } from 'react';
import {
  Box,
  Button,
  Card,
  DialogActions,
  TextField,
  Typography,
} from '@mui/material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import SettingsIcon from '@mui/icons-material/Settings';
import { useMutation } from '@apollo/client';
import {
  BrandKey,
  GenerateGoodDealArticleMutation,
  GenerateGoodDealArticleMutationVariables,
  PromptField,
  useGetPromptQuery,
  useUpdatePromptMutation,
} from '../../__generated__/queries-web';
import { GENERATE_GOOD_DEAL_ARTICLE_IA } from '../../apollo/mutations/generateGoodDealArticleIA.web.graphql';
import { client } from '../../apollo';
import { GET_ARTICLE_GOOD_DEAL_PROCESS } from '../../apollo/queries/getArticleGoodDealAiProcess.web.graphql';
import { GET_PROMPT } from '../../apollo/queries/prompts.web.graphql';
import { auth } from '../../utils/auth';

const PROMPT_LABEL = 'good-deal-article-generation';

export const ArticleGenerateForm = ({
  setActiveTab,
  brandKey,
}: {
  setActiveTab: React.Dispatch<React.SetStateAction<number>>;
  brandKey: BrandKey;
}) => {
  const [amazonUrl, setAmazonUrl] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [editedPromptBody, setEditedPromptBody] = useState<string | undefined>(
    undefined,
  );
  const [isSavingPrompt, setIsSavingPrompt] = useState(false);

  const { pushNotification } = useNotification();

  // Query to get the prompt
  const { data: promptData, loading: promptLoading } = useGetPromptQuery({
    variables: {
      brandKey,
      field: PromptField.Body,
      label: PROMPT_LABEL,
    },
  });

  const promptBody = editedPromptBody ?? promptData?.prompt?.body ?? '';

  // Mutation to update the prompt
  const [updatePrompt] = useUpdatePromptMutation({
    onCompleted: () => {
      setIsSavingPrompt(false);
      setEditedPromptBody(undefined); // Reset local edits, use refetched data
      pushNotification({
        type: NotificationTypeEnum.success,
        message: 'Prompt mis à jour avec succès',
      });
    },
    onError: (error) => {
      setIsSavingPrompt(false);
      pushNotification({
        type: NotificationTypeEnum.error,
        message: error.message || 'Erreur lors de la mise à jour du prompt',
      });
    },
  });

  const handleSavePrompt = () => {
    if (!promptData?.prompt?.id) {
      return;
    }
    setIsSavingPrompt(true);
    updatePrompt({
      variables: {
        where: {
          id: promptData.prompt.id,
        },
        data: {
          body: promptBody,
        },
      },
      refetchQueries: [
        {
          query: GET_PROMPT,
          variables: { brandKey, field: PromptField.Body, label: PROMPT_LABEL },
        },
      ],
    });
  };

  const [generateGoodDealArticleIA] = useMutation<
    GenerateGoodDealArticleMutation,
    GenerateGoodDealArticleMutationVariables
  >(GENERATE_GOOD_DEAL_ARTICLE_IA, {
    onCompleted: () => {
      setIsGenerating(false);
      setActiveTab(1);
      client.refetchQueries({
        include: [GET_ARTICLE_GOOD_DEAL_PROCESS],
      });
    },
    onError: (error) => {
      setIsGenerating(false);
      pushNotification({
        type: NotificationTypeEnum.error,
        message: error.message || 'Une erreur est survenue',
      });
    },
  });

  return (
    <>
      <form
        onSubmit={async (formEvent) => {
          formEvent.preventDefault();
          setIsGenerating(true);
          generateGoodDealArticleIA({
            variables: {
              productUrl: amazonUrl,
              brandKey,
            },
          });
        }}
      >
        <Card
          variant="outlined"
          sx={{
            padding: '40px',
            display: 'flex',
            flexDirection: 'column',
            gap: '32px',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              gap: '8px',
            }}
          >
            <AutoFixHighIcon
              fontSize="small"
              sx={{ marginRight: '2px', color: 'text.secondary' }}
            />
            <Typography
              sx={{ fontSize: 14, paddingTop: '1.5px' }}
              color="text.secondary"
              gutterBottom
            >
              Paramètres de l'article
            </Typography>
          </Box>
          <Box>
            <TextField
              sx={{
                width: '100%',
                mb: 3,
              }}
              id="outlined-error"
              label="Url de l'article"
              value={amazonUrl}
              onChange={(e) => setAmazonUrl(e.target.value)}
              required
            />
          </Box>

          <DialogActions>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
              }}
            >
              {isGenerating ? (
                <Loader />
              ) : (
                <Button
                  data-testid="confirmBtn"
                  color="primary"
                  variant="contained"
                  type="submit"
                >
                  {"Générer l'article"}
                </Button>
              )}
            </Box>
          </DialogActions>
        </Card>
      </form>

      {/* Prompt Editor Section */}
      {auth.user &&
        (auth.user.isSuperAdmin() ||
          [
            'kaitkaci@prismamedia.com',
            'arobert@prismamedia.com',
            'abillaul@prismamedia.com',
          ].includes(auth.user.email)) && (
          <Card
            variant="outlined"
            sx={{
              padding: '40px',
              display: 'flex',
              flexDirection: 'column',
              gap: '32px',
              marginTop: '20px',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                gap: '8px',
              }}
            >
              <SettingsIcon
                fontSize="small"
                sx={{ marginRight: '2px', color: 'text.secondary' }}
              />
              <Typography
                sx={{ fontSize: 14, paddingTop: '1.5px' }}
                color="text.secondary"
                gutterBottom
              >
                Prompt de génération
              </Typography>
            </Box>

            {promptLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Loader />
              </Box>
            ) : (
              <>
                <TextField
                  sx={{
                    width: '100%',
                  }}
                  label="Prompt"
                  value={promptBody}
                  onChange={(e) => setEditedPromptBody(e.target.value)}
                  multiline
                  minRows={6}
                  maxRows={20}
                />

                <DialogActions>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                    }}
                  >
                    {isSavingPrompt ? (
                      <Loader />
                    ) : (
                      <Button
                        color="primary"
                        variant="contained"
                        onClick={handleSavePrompt}
                        disabled={!promptBody}
                      >
                        Enregistrer le prompt
                      </Button>
                    )}
                  </Box>
                </DialogActions>
              </>
            )}
          </Card>
        )}
    </>
  );
};
